import { useCallback, useEffect, useMemo, useRef } from 'react';

import debounce from 'lodash/debounce';

import { useParametersUpdate } from '@queries';

type UseSyncParameterUpdatesParams = {
    layoutId: number;
    nodeId: string;
};

const DEBOUNCE = 1_000;

export const useSyncParameterUpdates = ({ layoutId, nodeId }: UseSyncParameterUpdatesParams) => {
    const pendingIdsRef = useRef<string[] | null>(null);
    const { mutateAsync: parametersUpdate } = useParametersUpdate(layoutId, nodeId);

    const flush = useCallback(async () => {
        if (!pendingIdsRef.current) return;
        const ids = pendingIdsRef.current;
        pendingIdsRef.current = null;
        await parametersUpdate(ids);
    }, [parametersUpdate]);

    const debouncedFlush = useMemo(() => debounce(flush, DEBOUNCE), [flush]);

    useEffect(() => {
        return () => {
            debouncedFlush.flush();
        };
    }, [debouncedFlush]);

    const scheduleSync = useCallback(
        (ids: string[]) => {
            pendingIdsRef.current = ids;
            debouncedFlush();
        },
        [debouncedFlush],
    );

    return {
        scheduleSync,
    };
};
